import { ProductInBuy } from '../context/buy_context'
import { CoinExchangeType, CoinInfo } from '../context/coin_context'
import { ConfigsInfo, ShowIdEnum } from '../context/configs_context'
import { BranchInfo } from '../context/branch_context'
import { BranchStockType, ProductInfo } from '../context/product_context'
import { ProductInPreSale, ProductInSale } from '../context/sale_context'
const currency = require('currency.js')

export function getIvaProp(iva: number, proporcao?: number): number {
    const value = Number(iva)
    if (value === 0) return 0
    return proporcao ?? 100
}

export function getIvaTasa(iva: number): number {
    return Number(iva)
}

export function getIvaBaseGrav(
    iva: number,
    subtotal: number,
    proporcao?: number
): number {
    const value = Number(iva)
    if (value === 0) return 0

    const prop = getIvaProp(value, proporcao)
    const tasa = getIvaTasa(value)

    return (100 * subtotal * prop) / (10000 + tasa * prop)
}

export function getIvaBaseExe(
    iva: number,
    subtotal: number,
    proporcao?: number
): number {
    const value = Number(iva)
    if (value === 0) return 0

    const prop = getIvaProp(value, proporcao)
    const tasa = getIvaTasa(value)

    return (100 * subtotal * (100 - prop)) / (10000 + tasa * prop)
}

export function getIvaAmount(
    iva: number,
    preco: number,
    quantidade: number,
    proporcao?: number
): number {
    const subtotal = preco * quantidade
    const value = Number(iva)

    if (value === 0) return 0

    const baseGrav = getIvaBaseGrav(value, subtotal, proporcao)
    const tasa = getIvaTasa(value)

    return (baseGrav * tasa) / 100
}

export function getIvaEffectiveRate(iva: number, proporcao?: number): number {
    const value = Number(iva)
    if (value === 0) return 0
    const prop = getIvaProp(value, proporcao)
    const tasa = getIvaTasa(value)
    return (prop * tasa) / 100
}

export function getIvaTaxableBase(
    iva: number,
    subtotal: number,
    proporcao?: number
): number {
    const value = Number(iva)
    if (value === 0) return 0
    return getIvaBaseGrav(value, subtotal, proporcao)
}

export function getIvaExemptBase(
    iva: number,
    subtotal: number,
    proporcao?: number
): number {
    const value = Number(iva)
    if (value === 0) return 0
    return getIvaBaseExe(value, subtotal, proporcao)
}

export function getIvaTenSubtotal(
    iva: number,
    preco: number,
    quantidade: number,
    proporcao?: number
): number {
    const value = Number(iva)
    if (value === 0) return 0
    if (value !== 10) return 0

    const prop = getIvaProp(value, proporcao)
    if (prop === 100) return preco * quantidade

    return (
        getIvaBaseGrav(value, preco * quantidade, proporcao) +
        getIvaAmount(value, preco, quantidade, proporcao)
    )
}

export function getTotalIvaCalculated(
    products: {
        iva: number | undefined
        proporcao?: number
        preco: number
        quantidade: number
    }[]
): number {
    return products.reduce(
        (total, current) =>
            currency(total, { precision: 15 }).add(
                getIvaAmount(
                    current.iva ?? 0,
                    current.preco,
                    current.quantidade,
                    current.proporcao
                )
            ).value,
        0
    )
}

export function getProductStockInBranch(
    product: ProductInfo,
    branch: BranchInfo | null | undefined,
    isMasterBranch: boolean
): number {
    if (!isMasterBranch) return Number(product.stock)

    const stockBranchId = branch?.stockBranchId ?? branch?.id

    if (!stockBranchId) return 0

    return (
        product.branchStocks?.find(
            (s) =>
                s.branchId === stockBranchId &&
                s.type === BranchStockType.Normal
        )?.stock ?? 0
    )
}

export function maskCurrency(
    value: string | number,
    coin?: CoinInfo | null,
    digits: number = 2,
    locale = 'es-Py'
): string {
    if (coin?.hideDecimal) digits = 0

    if (typeof value === 'number')
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(value)

    const numberValue = Number(value)

    if (isNaN(numberValue)) return '0,00'

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(numberValue)
}

export function currencyToNumber(value: string, digits: number = 2): number {
    value = value.replace(/\D/g, '')
    const regex = new RegExp(`(\\d)(\\d{${digits}})$`)
    value = value.replace(regex, `$1.$2`)
    return Number(value)
}

export function removeExcessSpaces(value: string): string {
    return value.trim().replace(/\s\s+/g, ' ')
}

export function convertFileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            if (
                e.target &&
                e.target.result &&
                typeof e.target.result === 'string'
            )
                resolve(e.target.result)
            else reject()
        }
        reader.readAsDataURL(file)
    })
}

export function convertBase64ToFile(base64: string): Promise<File> {
    return new Promise<File>((resolve, reject) => {
        const arr = base64.split(',')

        const mime = arr[0].match(/:(.*?);/)?.pop()
        if (!mime) {
            reject()
            return
        }

        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
        }

        const file = new File([u8arr], 'imagem_camera.png', { type: mime })
        resolve(file)
    })
}

export function getDateInDDMMYYYYFormat(date: Date, getHours?: boolean) {
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()

    if (getHours) {
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        const ss = String(date.getSeconds()).padStart(2, '0')

        return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`
    }

    return `${dd}/${mm}/${yyyy}`
}

export function getDateInIsoFormat(date: Date, getHours?: boolean) {
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()

    if (getHours) {
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        const ss = String(date.getSeconds()).padStart(2, '0')

        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
    }

    return `${yyyy}-${mm}-${dd}`
}

export function getExchangeValue({
    amount,
    exchange,
    coinExchangeType,
    order,
}: {
    amount: number
    exchange: number
    coinExchangeType: number
    order: 'normal' | 'reverse'
}): number {
    if (order === 'normal') {
        return coinExchangeType === CoinExchangeType.Normal
            ? currency(amount).multiply(exchange).value
            : currency(amount).divide(exchange).value
    } else {
        return coinExchangeType === CoinExchangeType.Normal
            ? currency(amount).divide(exchange).value
            : currency(amount).multiply(exchange).value
    }
}

export function getExchangeDiference({
    finalAmount,
    amount,
    coinExchangeType,
}: {
    finalAmount: number
    amount: number
    coinExchangeType: number
}): number {
    return coinExchangeType === CoinExchangeType.Normal
        ? currency(finalAmount).divide(amount).value
        : currency(amount).divide(finalAmount).value
}

interface GetDateString {
    date?: string
    hour?: string
    dateObj?: Date
    configs: ConfigsInfo
    isSummerTime: boolean
}

export function getDateString(data: GetDateString) {
    let date: Date | null = null

    if (data.date && data.hour) {
        date = new Date(`${data.date.split('T')[0]} ${data.hour}`)
    } else if (data.dateObj) {
        date = new Date(data.dateObj)
    }

    if (date) {
        const summerTime = data.isSummerTime ? 1 : 0
        date.setHours(date.getHours() + data.configs.timeZone + summerTime)
    }

    const dateString = date?.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
    const hourString = date?.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })

    return `${dateString} ${hourString}`
}

interface GetTimeAgo {
    date: string
    configs: ConfigsInfo
    isSummerTime: boolean
}

//Traduzir retorno
export function getTimeAgo(data: GetTimeAgo) {
    const currentDate = new Date()
    const date = new Date(data.date)

    const summerTime = data.isSummerTime ? 1 : 0

    const offset = -(currentDate.getTimezoneOffset() / 60)
    currentDate.setHours(
        currentDate.getHours() -
            offset +
            data.configs.timeZone +
            Number(data.configs.isSummerTime)
    )

    date.setHours(date.getHours() + data.configs.timeZone + summerTime)

    const seconds = Math.floor((currentDate.valueOf() - date.valueOf()) / 1000)

    let interval = seconds / 31536000

    if (interval > 1) {
        const floorInterval = Math.floor(interval)
        return `${floorInterval} ano${floorInterval !== 1 ? 's' : ''}`
    }
    interval = seconds / 2592000
    if (interval > 1) {
        const floorInterval = Math.floor(interval)
        return `${floorInterval} mes${floorInterval !== 1 ? 'es' : ''}`
    }
    interval = seconds / 86400
    if (interval > 1) {
        const floorInterval = Math.floor(interval)
        return `${floorInterval} dia${floorInterval !== 1 ? 's' : ''}`
    }
    interval = seconds / 3600
    if (interval > 1) {
        const floorInterval = Math.floor(interval)
        return `${floorInterval} hora${floorInterval !== 1 ? 's' : ''}`
    }
    interval = seconds / 60
    if (interval > 1) {
        const floorInterval = Math.floor(interval)
        return `${floorInterval} minuto${floorInterval !== 1 ? 's' : ''}`
    }
    interval = seconds
    const floorInterval = Math.floor(interval)
    return `${floorInterval} segundo${floorInterval !== 1 ? 's' : ''}`
}

export interface ListReturn<T> {
    data: T[]
    count: number
}

export function validateRuc(ruc: string) {
    return /^\d{6,8}-\d$/.test(ruc) || /^\d{6,8}$/.test(ruc)
}

export function validateEmail(email: string): boolean {
    return /^[\w\.-]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/.test(email)
}

export function normalizeString(v: string) {
    return v.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

export function generateRandomColor() {
    const randomRGB = () => Math.floor(Math.random() * 255)

    return `rgba(${randomRGB()}, ${randomRGB()}, ${randomRGB()}, 0.7)`
}

export const getNoteNameCharLimit = (configs: ConfigsInfo) => {
    if (configs.showBarcodeOnNote && configs.showIdOnNote) return 50
    if (configs.showBarcodeOnNote || configs.showIdOnNote) return 70
    return 95
}

export function calcularDv(ruc: string): string {
    if (!/^[0-9]+$/.test(ruc)) {
        return ''
    }

    let v_caracter = ''
    let v_numero_al = ''

    let k = 2
    let v_total = 0
    let v_numero_aux
    let v_resto
    let v_digito

    for (let i = 0; i < ruc.length; i++) {
        v_caracter = ruc.toUpperCase().substring(i, i + 1)
        let v_caracterAscii = v_caracter.charCodeAt(0)
        if (v_caracterAscii >= 48 && v_caracterAscii <= 57) {
            v_numero_al += v_caracter
        } else {
            v_numero_al += v_caracterAscii
        }
    }

    for (let i = v_numero_al.length; i > 0; i--) {
        if (k > 11) {
            k = 2
        }
        let digito = v_numero_al.substring(i - 1, i)
        v_numero_aux = parseInt(digito)
        v_total += v_numero_aux * k

        k++
    }

    v_resto = v_total % 11
    if (v_resto > 1) {
        v_digito = 11 - v_resto
    } else {
        v_digito = 0
    }

    return v_digito.toString()
}

export function mmToPx(mm: number) {
    const dpi = getDPI()
    return (mm / 25.4) * dpi
}

export function getDPI() {
    const div = document.createElement('div')
    div.style.width = '1in'
    div.style.height = '1in'
    div.style.position = 'absolute'
    div.style.top = '-100%'
    document.body.appendChild(div)
    const dpi = div.offsetWidth
    document.body.removeChild(div)
    return dpi
}

export function setRecursiveValueObject(
    data: any,
    value: any,
    checkValue?: any
) {
    if (typeof data !== 'object') return

    const keys = Object.keys(data)

    for (const key of keys) {
        if (typeof data[key] === 'object') {
            data[key] = setRecursiveValueObject(data[key], value, checkValue)
            if (
                typeof checkValue !== 'undefined' &&
                !Object.keys(data[key]).length
            )
                delete data[key]
        } else {
            if (typeof checkValue !== 'undefined' && data[key] !== checkValue)
                delete data[key]
            else data[key] = value
        }
    }

    return data
}

export function breakText(text: string, limit: number) {
    while (text.split('\n').some((line) => line.length > limit)) {
        text = text
            .split('\n')
            .map((line) => {
                if (line.length <= limit) return line
                const firstPart = line.slice(0, limit - 1)
                const secondPart = line.slice(limit - 1)
                return `${firstPart}\n${secondPart}`
            })
            .join('\n')
    }

    return text
}

export function getNameText(product: ProductInfo, configs: ConfigsInfo) {
    return configs.showId === ShowIdEnum.InternalCode
        ? `${product.internalCodes[0]} / ${product.parsedName}`
        : configs.showId === ShowIdEnum.Barcode
          ? `${product.barcodes[0]} / ${product.parsedName}`
          : product.parsedName
}

export function getNumberOfItensText(
    products: (ProductInPreSale | ProductInSale | ProductInBuy)[]
) {
    const groupedQnt: { unitType: string; qnt: number }[] = []

    for (const product of products) {
        const foundGroupedQnt = groupedQnt.find(
            (gq) => gq.unitType === product.product.unitType
        )
        if (foundGroupedQnt) foundGroupedQnt.qnt += product.qnt
        else
            groupedQnt.push({
                unitType: product.product.unitType,
                qnt: product.qnt,
            })
    }

    return groupedQnt.length
        ? `${products.length} (${groupedQnt
              .map((gq) => `${gq.qnt} ${gq.unitType}`)
              .join(', ')})`
        : '0'
}

export function quandoImagensCarregarem(container: HTMLElement) {
    return new Promise<void>((resolve) => {
        if (!container) {
            console.error(`O elemento não foi encontrado no DOM.`)
            return resolve()
        }

        const imagens = Array.from(container.querySelectorAll('img'))

        if (imagens.length === 0) {
            return resolve()
        }

        const promessasDasImagens = imagens.map((img) => {
            return new Promise<void>((resolveImg) => {
                if (img.complete) {
                    resolveImg()
                } else {
                    img.addEventListener('load', () => resolveImg())
                    img.addEventListener('error', () => resolveImg())
                }
            })
        })

        Promise.all(promessasDasImagens).then(() => resolve())
    })
}

export function numeroPorExtensoEspanhol(
    num: number,
    selectedCoin?: CoinInfo,
    opcaoDecimal = 'centavos'
) {
    // Garante duas casas decimais e separa inteiro/decimal
    const partes = parseFloat(num.toString()).toFixed(2).split('.')
    const parteInteira = parseInt(partes[0], 10)
    const parteDecimal = parseInt(partes[1], 10)

    const UNIDADES = [
        '',
        'uno',
        'dos',
        'tres',
        'cuatro',
        'cinco',
        'seis',
        'siete',
        'ocho',
        'nueve',
    ]
    const DECENAS_10 = [
        'diez',
        'once',
        'doce',
        'trece',
        'catorce',
        'quince',
        'dieciséis',
        'diecisiete',
        'dieciocho',
        'diecinueve',
    ]
    const DECENAS_20 = [
        'veinte',
        'veintiuno',
        'veintidós',
        'veintitrés',
        'veinticuatro',
        'veinticinco',
        'veintiséis',
        'veintisiete',
        'veintiocho',
        'veintinueve',
    ]
    const DECENAS = [
        '',
        '',
        '',
        'treinta',
        'cuarenta',
        'cincuenta',
        'sesenta',
        'setenta',
        'ochenta',
        'noventa',
    ]
    const CENTENAS = [
        '',
        'ciento',
        'doscientos',
        'trescientos',
        'cuatrocientos',
        'quinientos',
        'seiscientos',
        'setecientos',
        'ochocientos',
        'novecientos',
    ]

    function tresDigitos(n: number, esMilOMillon = false) {
        if (n === 0) return ''
        if (n === 100) return 'cien'

        let c = Math.floor(n / 100)
        let resto = n % 100
        let strCentena = CENTENAS[c]

        let strDecena = ''
        if (resto > 0) {
            if (resto < 10) {
                strDecena = UNIDADES[resto]
            } else if (resto < 20) {
                strDecena = DECENAS_10[resto - 10]
            } else if (resto < 30) {
                strDecena = DECENAS_20[resto - 20]
            } else {
                let d = Math.floor(resto / 10)
                let u = resto % 10
                strDecena = DECENAS[d] + (u > 0 ? ' y ' + UNIDADES[u] : '')
            }
        }

        let res = (strCentena ? strCentena + ' ' : '') + strDecena

        // Regra da apócope antes de 'mil' ou 'millón'
        if (esMilOMillon) {
            res = res.replace(/veintiuno$/, 'veintiún').replace(/uno$/, 'un')
        }

        return res.trim()
    }

    function converterInteiro(n: number): string {
        if (n === 0) return 'cero'
        if (n < 0) return 'menos ' + converterInteiro(Math.abs(n))

        if (n < 1000) {
            return tresDigitos(n)
        }

        if (n < 1000000) {
            let miles = Math.floor(n / 1000)
            let resto = n % 1000
            let strMiles =
                miles === 1 ? 'mil' : tresDigitos(miles, true) + ' mil'
            let strResto = tresDigitos(resto)
            return (strMiles + ' ' + strResto).trim()
        }

        if (n < 1000000000) {
            let millones = Math.floor(n / 1000000)
            let resto = n % 1000000
            let strMillones =
                millones === 1
                    ? 'un millón'
                    : tresDigitos(millones, true) + ' millones'
            let strResto = resto > 0 ? converterInteiro(resto) : ''
            return (strMillones + ' ' + strResto).trim()
        }

        return 'Número fora do limite suportado'
    }

    const textoInteiro = converterInteiro(parteInteira)
    const textoMoeda = selectedCoin
        ? ' ' + selectedCoin?.name.toLowerCase() + (num !== 1 ? 'es' : '')
        : ''

    // Se não houver parte decimal (00 centavos)
    if (parteDecimal === 0) {
        return `${textoInteiro}${textoMoeda}`
    }

    // Formatação dos centavos/decimais
    let textoDecimal = ''
    if (opcaoDecimal === 'fracao') {
        textoDecimal = `${partes[1]}/100`
    } else {
        let extensoDecimal = converterInteiro(parteDecimal)
        let unidade = parteDecimal === 1 ? 'céntimo' : 'centavos'
        textoDecimal = `${extensoDecimal} ${unidade}`
    }

    return `${textoInteiro}${textoMoeda} con ${textoDecimal}`
}
